import { z } from 'zod';
import {
  DEPARTMENT_STATUSES,
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  PROBATION_STATUSES,
  ROLES,
  USER_STATUSES,
} from '../enums.js';
import {
  dayString,
  departmentRef,
  email,
  employeeRef,
  isoDateTime,
  listQueryWith,
  nonEmpty,
  objectId,
  password,
  timestamps,
} from './common.js';

/** Auth, users, employees, departments and the reporting hierarchy. */

// ── Auth ────────────────────────────────────────────────────────────────────────

export const loginBody = z.object({
  email,
  password: z.string().min(1, 'Password is required'),
});

/**
 * The signed-in user as the client needs them.
 *
 * `permissions` is the resolved list — role grants plus manager and department-head
 * grants derived from organizational facts. The frontend renders from this rather than
 * inferring anything from the role name.
 */
export const sessionUser = z.object({
  id: objectId,
  email: z.string(),
  role: z.enum(ROLES),
  status: z.enum(USER_STATUSES),
  mustChangePassword: z.boolean(),
  employee: employeeRef.nullable(),
  permissions: z.array(z.string()),
  hasDirectReports: z.boolean(),
  headsDepartmentIds: z.array(objectId),
});
export type SessionUser = z.infer<typeof sessionUser>;

export const activationInfoQuery = z.object({ token: z.string().min(1) });

export const activationInfo = z.object({
  email: z.string(),
  fullName: z.string(),
  expiresAt: isoDateTime,
});

export const activateBody = z.object({ token: z.string().min(1), password });

export const changePasswordBody = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: password,
});

export const forgotPasswordBody = z.object({ email });
export const resetPasswordBody = z.object({ token: z.string().min(1), password });

export const sessionRecord = z.object({
  id: objectId,
  current: z.boolean(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  lastUsedAt: isoDateTime,
  createdAt: isoDateTime,
});

export const logoutBody = z.object({ allDevices: z.boolean().default(false) });

// ── Employees ───────────────────────────────────────────────────────────────────

const addressShape = z.object({
  line1: z.string().max(200).nullable(),
  line2: z.string().max(200).nullable(),
  city: z.string().max(100).nullable(),
  state: z.string().max(100).nullable(),
  postalCode: z.string().max(20).nullable(),
  country: z.string().max(100).nullable(),
});

const emergencyContactShape = z.object({
  name: z.string().max(120).nullable(),
  relationship: z.string().max(60).nullable(),
  phone: z.string().max(30).nullable(),
});

/**
 * Bank and statutory identifiers.
 *
 * MASKED by default — the full value requires `employees.readSensitive`, and every
 * reveal is audited. `masked: true` tells the UI to render the placeholder treatment
 * rather than guessing from the string's shape.
 */
export const sensitiveFields = z.object({
  masked: z.boolean(),
  bank: z.object({
    accountNumber: z.string().nullable(),
    ifsc: z.string().nullable(),
    bankName: z.string().nullable(),
  }),
  statutory: z.object({
    pan: z.string().nullable(),
    pf: z.string().nullable(),
    uan: z.string().nullable(),
    esi: z.string().nullable(),
  }),
});

export const employee = z
  .object({
    id: objectId,
    employeeCode: z.string(),
    fullName: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    workEmail: z.string(),
    personalEmail: z.string().nullable(),
    personalPhone: z.string().nullable(),
    avatarUrl: z.string().nullable(),

    jobTitle: z.string().nullable(),
    department: departmentRef.nullable(),
    manager: employeeRef.nullable(),
    employmentType: z.enum(EMPLOYMENT_TYPES).nullable(),
    joiningDate: dayString.nullable(),
    exitDate: dayString.nullable(),
    probationStatus: z.enum(PROBATION_STATUSES),
    probationEndDate: dayString.nullable(),
    status: z.enum(EMPLOYEE_STATUSES),

    /** The portal account, which has its own lifecycle. */
    account: z.object({
      userId: objectId.nullable(),
      role: z.enum(ROLES).nullable(),
      status: z.enum(USER_STATUSES).nullable(),
      invitedAt: isoDateTime.nullable(),
      activatedAt: isoDateTime.nullable(),
    }),

    dateOfBirth: dayString.nullable(),
    permanentAddress: addressShape.nullable(),
    currentAddress: addressShape.nullable(),
    emergencyContact: emergencyContactShape.nullable(),
    sensitive: sensitiveFields.nullable(),
  })
  .merge(timestamps);
export type Employee = z.infer<typeof employee>;

/** Row shape for the directory. Narrower than the full record on purpose. */
export const employeeListItem = z.object({
  id: objectId,
  employeeCode: z.string(),
  fullName: z.string(),
  workEmail: z.string(),
  jobTitle: z.string().nullable(),
  department: departmentRef.nullable(),
  manager: employeeRef.nullable(),
  status: z.enum(EMPLOYEE_STATUSES),
  accountStatus: z.enum(USER_STATUSES).nullable(),
  joiningDate: dayString.nullable(),
  avatarUrl: z.string().nullable(),
});

export const employeeListQuery = listQueryWith({
  departmentId: objectId.optional(),
  managerId: objectId.optional(),
  status: z.enum(EMPLOYEE_STATUSES).optional(),
  employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
});

export const createEmployeeBody = z.object({
  fullName: nonEmpty('Full name', 120),
  workEmail: email,
  jobTitle: z.string().max(120).optional(),
  departmentId: objectId,
  managerId: objectId.optional(),
  employmentType: z.enum(EMPLOYMENT_TYPES).default('full_time'),
  joiningDate: dayString,
  /** Restricted by `canAssignRole`; HR may only create EMPLOYEE. */
  role: z.enum(ROLES).default('EMPLOYEE'),
  sendInvitation: z.boolean().default(true),
});

/** Create response — includes a one-time password when the invitation email is skipped. */
export const employeeCreated = employee.extend({
  temporaryPassword: z.string().optional(),
});

/** Admin-issued temporary password (shown once in the response, never stored). */
export const temporaryPasswordIssued = z.object({
  workEmail: z.string(),
  temporaryPassword: z.string(),
});

/** Set or reset the portal sign-in password from the employee account tab. */
export const issueTemporaryPasswordBody = z
  .object({
    password: password.optional(),
    confirmPassword: z.string().optional(),
    /** When true, the employee must choose a new password after signing in. Default true. */
    mustChangePassword: z.boolean().default(true),
  })
  .superRefine((value, ctx) => {
    const hasPassword = Boolean(value.password);
    const hasConfirm = Boolean(value.confirmPassword);
    if (hasPassword !== hasConfirm) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter and confirm the new password, or leave both blank to generate one',
        path: ['confirmPassword'],
      });
      return;
    }
    if (value.password && value.password !== value.confirmPassword) {
      ctx.addIssue({
        code: 'custom',
        message: 'Both passwords must match',
        path: ['confirmPassword'],
      });
    }
  });

/**
 * Field allow-list for an update.
 *
 * Deliberately NOT a partial of the full record: a generic "patch anything" endpoint is
 * how `status` and account fields become reachable by roles that should not touch them.
 * Account lifecycle and role changes have their own endpoints with their own permissions.
 */
export const updateEmployeeBody = z
  .object({
    fullName: nonEmpty('Full name', 120),
    firstName: z.string().max(60).nullable(),
    lastName: z.string().max(60).nullable(),
    personalEmail: email.nullable(),
    personalPhone: z.string().max(30).nullable(),
    jobTitle: z.string().max(120).nullable(),
    departmentId: objectId,
    employmentType: z.enum(EMPLOYMENT_TYPES),
    joiningDate: dayString,
    probationEndDate: dayString.nullable(),
    dateOfBirth: dayString.nullable(),
    permanentAddress: addressShape,
    currentAddress: addressShape,
    emergencyContact: emergencyContactShape,
  })
  .partial();

/** The narrower allow-list an employee may apply to their own record (Q-06). */
export const updateOwnProfileBody = updateEmployeeBody.pick({
  personalEmail: true,
  personalPhone: true,
  currentAddress: true,
  emergencyContact: true,
});

export const setEmployeeStatusBody = z.object({
  status: z.enum(EMPLOYEE_STATUSES),
  exitDate: dayString.optional(),
  reason: z.string().max(500).optional(),
});

export const assignManagerBody = z.object({
  managerId: objectId.nullable(),
});

export const assignRoleBody = z.object({
  role: z.enum(ROLES),
});

export const employeeHistoryEntry = z.object({
  id: objectId,
  field: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
  changedBy: employeeRef.nullable(),
  changedAt: isoDateTime,
});

export const reportingMeta = z.object({
  managerChain: z.array(employeeRef),
  directReports: z.array(employeeRef),
});

/** Org chart and company directory — no addresses, DOB, or personal contact details. */
export const hierarchyNode = z.object({
  id: objectId,
  fullName: z.string(),
  employeeCode: z.string(),
  workEmail: z.string(),
  jobTitle: z.string().nullable(),
  departmentName: z.string().nullable(),
  managerId: objectId.nullable(),
  managerName: z.string().nullable(),
  joiningDate: dayString.nullable(),
  avatarUrl: z.string().nullable(),
  /** Portal role when linked — used to order leadership; operators are omitted from this list. */
  portalRole: z.enum(ROLES).nullable(),
});

// ── Departments ─────────────────────────────────────────────────────────────────

export const department = z
  .object({
    id: objectId,
    name: z.string(),
    code: z.string().nullable(),
    description: z.string().nullable(),
    departmentHead: employeeRef.nullable(),
    employeeCount: z.number().int(),
    status: z.enum(DEPARTMENT_STATUSES),
  })
  .merge(timestamps);

export const createDepartmentBody = z.object({
  name: nonEmpty('Department name', 120),
  code: z.string().max(20).optional(),
  description: z.string().max(500).optional(),
  departmentHeadEmployeeId: objectId.optional(),
});

export const updateDepartmentBody = createDepartmentBody.partial();

export const assignDepartmentHeadBody = z.object({
  employeeId: objectId.nullable(),
});

// ── Work schedules ──────────────────────────────────────────────────────────────

export const workSchedule = z
  .object({
    id: objectId,
    name: z.string(),
    isDefault: z.boolean(),
    /** 0 = Sunday. An organization's working week is configuration, not an assumption. */
    workingDays: z.array(z.number().int().min(0).max(6)),
    startTime: z.string(),
    endTime: z.string(),
    requiredMinutesPerDay: z.number().int(),
    flexible: z.boolean(),
    graceMinutes: z.number().int(),
    timezone: z.string(),
  })
  .merge(timestamps);

export const createWorkScheduleBody = workSchedule
  .omit({ id: true, createdAt: true, updatedAt: true })
  .partial({ isDefault: true, flexible: true, graceMinutes: true });
